<template>
  <div
    class="mobile-size-selector"
    :class="{ [$class('selected')]: variant !== null }"
  >
    <div
      v-if="showSize && state != 'added'"
      class="mobile-size-selector__body"
      :aria-expanded="showSize"
    >
      <template
        v-for="(variantObject, index) of sortedSizes"
        :key="index"
      >
        <button
          class="mobile-size-selector__button"
          :class="{
            'mobile-size-selector__button--oos': !variantObject.available,
            'mobile-size-selector__button--low-stock':
              variantObject.available && variantObject.inventory < lowAvailabilityThreshold,
            [$class('active')]: variant?.id === variantObject.id,
          }"
          type="button"
          @click="handleVariantClick(variantObject)"
        >
          <span
            class="
              text-mobile-medium-uc-mobile
              mobile-size-selector__button-label
            "
            v-text="variantObject?.title"
          />

          <span
            v-if="!variantObject.available"
            class="text-desktop-14 mobile-size-selector__button-alert"
            v-text="$string('product.cta.notify_me_mobile')"
          />
          <span
            v-if="variantObject.available && variantObject.inventory < lowAvailabilityThreshold"
            class="text-desktop-14 mobile-size-selector__button-alert"
            v-text="$string('product.cta.low_availability')"
          />
        </button>
      </template>
    </div>

    <template v-else>
      <div
        class="mobile-size-selector__message text-mobile-regular-mobile"
        v-text="$string('product.cta.mobile_added_to_cart', {
          replace: {
            variant: variant.title,
          },
        })
        "
      />

      <div
        class="mobile-size-selector__footer main-product__form-footer-action"
        :class="{
          'mobile-size-selector__footer--no-border': state === 'added',
        }"
      >
        <button
          type="button"
          class="
            mobile-size-selector__footer-button
            mobile-size-selector__footer-action
            text-mobile-small-uc-mobile text-bold
          "
          @click="openCartDrawer"
          v-text="$string('product.cta.view_cart')"
        />
      </div>
    </template>

    <div
      v-if="state != 'added'"
      class="mobile-size-selector__footer main-product__form-footer-action"
    >
      <p
        v-if="cartError"
        class="mobile-size-selector__error text-desktop-small text-mobile-small"
        v-text="cartError"
      />

      <div class="mobile-size-selector__footer-content">
        <span
          v-if="sizeGuideFitLabel"
          class="text-mobile-12-mobile"
          v-text="sizeGuideFitLabel"
        />

        <template v-if="isSizeGuideAvailable">
          <icon-chevron-right-special />
          <button
            class="mobile-size-selector__footer-button text-mobile-small-mobile"
            type="button"
            @click="
              openOverlay({
                component: 'size-guide',
                ignoreDismissed: true,
                namespace: 'sizeGuide',
              }),
              () => handleClick(null, true)
            "
            v-text="$string('product.cta.size_guide')"
          />
        </template>
      </div>
      <store-availability
        :product="product"
        :variant="variant"
        :store-availability-content="storeAvailabilityContent"
        :use-store-availability-from-locations="useStoreAvailabilityFromLocations"
        :coordinates="coordinates"
        :variant-availability-data="variantAvailabilityData"
        is-trigger
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Mobile size selector (mobile-size-selector)
 * -----------------------------------------------------------------------------
 * Displays the various UI elements for the mobile PDP.
 *
 * @param {Object} product - Shopify product object.
 * @param {Boolean} isSizeGuideAvailable - Boolean indicating is size guide
 * has content to display.
 * @param {String} sizeGuideFitLabel - Fit label for the size guide.
 * @param {Array} sizeOrder - Array of size order strings.
 * @param {Object} storeAvailabilityContent - Store availability settings.
 *
 */

import { mapActions, mapGetters, mapState } from 'vuex'
import IconChevronRightSpecial from '~icons/direction/chevron/right-special.svg'
import StoreAvailability from '~async/store-availability/store-availability'

export default {
  name: 'MobileSizeSelector',
  components: {
    IconChevronRightSpecial,
    StoreAvailability,
  },
  props: {
    product: {
      type: [Boolean, Object],
      default: false,
    },

    isSizeGuideAvailable: {
      type: Boolean,
      default: false,
    },

    sizeGuideFitLabel: {
      type: String,
      default: '',
    },

    sizeOrder: {
      type: Array,
      default: () => [],
    },

    storeAvailabilityContent: {
      type: Object,
      default: () => ({}),
    },

    useStoreAvailabilityFromLocations: {
      type: Boolean,
      default: false,
    },

    coordinates: {
      type: Object,
      default: () => ({
        latitude: Number,
        longitude: Number,
      }),
    },

    variantAvailabilityData: {
      type: Array,
      default: () => [],
    },

    lowAvailabilityThreshold: {
      type: Number,
      default: 3,
    },
  },

  emits: {
    'show-mobile-notify': (payload) => typeof payload === 'object',
    'size-selector-action': (payload) => typeof payload === 'object',
    'size-selector-close': (payload) => payload,
  },

  data() {
    return {
      variant: null,
      showNotify: false,
      showSize: true,
      state: 'ready',
      quantity: 1,
      lineItemProperties: {},
      cartError: null,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Computes sorted product variants.
     * @returns {Array}
     */
    sortedSizes() {
      if (!this.product?.variants?.length) {
        return []
      }

      const newProduct = JSON.parse(JSON.stringify(this.product))

      if (!Number(newProduct.variants[0].title)) {
        return newProduct.variants.sort((a, b) => {
          return (
            this.sizeOrder.indexOf(a.title) - this.sizeOrder.indexOf(b.title)
          )
        })
      }

      return newProduct.variants.sort((a, b) => a.title - b.title)
    },
  },

  watch: {

    /**
     * When the variant updates emit the new value to the parent.
     */
    variant(variant) {
      const event = {
        action: 'variant-updated',
        variant,
      }
      this.$emit('size-selector-action', event)
      this.cartError = null
    },
  },

  mounted() {
    cnvs.EventBus.listen('atc-clicked', () => {
      this.showSize = true
    })

    this.setEventListeners()
  },

  methods: {

    /**
     * Set event listeners.
     */
    setEventListeners() {

      /**
       * Add default listeners.
       */
      document.addEventListener('click', (event) => {
        if (
          this.state === 'added' &&
          !event.target.closest('.mobile-size-selector')
        ) {
          setTimeout(() => {
            this.resetState()
            this.resetComponent()
          }, 300)
        }
      })
    },

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      addItem: 'cart/addItem',
      openOverlay: 'overlays/open',
    }),

    /**
     * Handle add to cart submission.
     * @param {Event} event - Click event.
     */
    async handleAddToCart(variant) {
      if (!variant || this.state !== 'ready') {
        return
      }

      clearTimeout(this.timeout)
      this.error = ''
      this.state = 'adding'

      const items = [
        {
          id: variant.id,
          properties: this.lineItemProperties,
          quantity: this.quantity,
        },
      ]

      try {
        await this.addItem(items)
        this.state = 'added'
      } catch (error) {
        this.state = 'error'

        if (error?.description) {
          this.cartError = error.description

          setTimeout(() => {
            this.resetState()
          }, 300)

          return
        }

        reportError('Failed to add product to cart', error)
      }
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    resetState() {
      this.state = 'ready'
    },

    /**
     * Handles clicks on a variant product.
     * @param {Object} variant - Shopify product variant.
     */
    handleVariantClick(variant) {
      if (variant?.available) {
        this.variant = variant

        this.handleAddToCart(variant)
        return
      }

      this.$store.dispatch('product/setVariant', variant)
      this.$emit('show-mobile-notify', variant)

      this.openOverlay({
        component: 'back-in-stock',
        ignoreDismissed: true,
        namespace: 'backInStock',
      })
    },

    handleAtcClick() {
      this.showSize = true
    },

    /**
     * Show cart crawer
     */
    openCartDrawer() {
      if (this.getComponentExists('cart-drawer')) {
        this.openOverlay({
          component: 'cart-drawer',
          ignoreDismissed: true,
          namespace: 'cartDrawer',
        })

        const header = document.querySelector('.site-header')
        if (!header) {
          return
        }

        // Show site-header
        if (this.isMobile) {
          document.documentElement.setAttribute('data-scrolldir', 'up')
        }
      }
    },

    /**
     * Resets the component to its initial state.
     * - Called by parent AFTER add-to-cart.
     */
    resetComponent() {
      this.variant = null
      this.showNotify = false
      this.showSize = true
    },
  },
}
</script>

<style lang="scss">
@import "./mobile-size-selector";
</style>
